/*
list Achievers
 filter by course (chips, derived from the achievers payload)
 add pagination
 use redux
 */

import { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
// @mui
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import { MotionInView, varFadeInUp } from '../../components/animate';
import { SectionHeader } from '../../sections/web/common';
import { Seo, collectionSchema } from '../../components/seo';
import { StudentCard } from '../../sections/web/home';
// hooks
import useResponsive from '../../hooks/useResponsive';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { getAchievers } from '../../redux/slices/course';

// ----------------------------------------------------------------------
const ITEMS_PER_PAGE = 12;

// `course` is a plain name string from the API, but stay tolerant of an
// object shape so a serializer change can't blank the whole filter.
const getCourseName = (student) => {
  const { course } = student ?? {};

  if (!course) {
    return '';
  }

  return typeof course === 'string' ? course : course.name ?? '';
};

export default function AchieversPage() {
  const dispatch = useDispatch();
  const { achievers, isLoading } = useSelector((state) => state.course);
  const isUpSm = useResponsive('up', 'sm');

  const [courseFilter, setCourseFilter] = useState('');
  const [page, setPage] = useState(1);

  // One unfiltered fetch. The course options are derived from this payload and
  // the filtering happens locally, so the page does not depend on the separate
  // courses endpoint (or on the API's server-side filter behaviour).
  useEffect(() => {
    dispatch(getAchievers());
  }, [dispatch]);

  const allAchievers = useMemo(() => (Array.isArray(achievers) ? achievers : []), [achievers]);

  // Unique courses with their achiever counts, alphabetical.
  const courseOptions = useMemo(() => {
    const counts = new Map();

    allAchievers.forEach((student) => {
      const name = getCourseName(student);
      if (name) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    });

    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [allAchievers]);

  const filtered = useMemo(
    () => (courseFilter ? allAchievers.filter((student) => getCourseName(student) === courseFilter) : allAchievers),
    [allAchievers, courseFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const firstIndex = filtered.length ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const lastIndex = (currentPage - 1) * ITEMS_PER_PAGE + paged.length;

  const showLoading = isLoading && !allAchievers.length;

  const handleCourseFilter = (value) => {
    setCourseFilter(value);
    setPage(1);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filterChips = [
    { value: '', label: 'All', count: allAchievers.length },
    ...courseOptions.map((course) => ({ value: course.name, label: course.name, count: course.count })),
  ];

  return (
    <>
      <Seo
        title="DHA, MOH & HAAD Successful Students"
        description="Meet the healthcare professionals who passed their DHA, MOH and HAAD licensing exams after training with ASAP Marketing and Studies Consultancy."
        path="/achievers"
        // Achievers have no individual route, so the list entries carry names
        // only — no invented URLs.
        jsonLd={collectionSchema({
          name: 'DHA, MOH & HAAD Successful Students',
          description:
            'Healthcare professionals who passed their DHA, MOH and HAAD licensing exams with ASAP Marketing and Studies Consultancy.',
          path: '/achievers',
          items: allAchievers
            .map((student) => student?.user?.name)
            .filter(Boolean)
            .map((name) => ({ name })),
        })}
      />

      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <SectionHeader
          eyebrow="Our achievers"
          title="Achievers"
          titleComponent="h1"
          description="Meet some of the healthcare professionals who trained with ASAP and went on to pass their licensing exams across the GCC."
        />

        <MotionInView variants={varFadeInUp}>
          <Card sx={{ p: { xs: 1.75, md: 2.5 }, mb: { xs: 2.5, md: 3 } }}>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mb: 1.5, px: { xs: 0.5, md: 0.25 }, alignItems: 'center' }}
            >
              <Iconify icon="ic:round-filter-list" width={18} sx={{ color: 'text.disabled' }} />
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                Filter by course
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled', ml: 'auto', whiteSpace: 'nowrap' }}>
                {filtered.length} {filtered.length === 1 ? 'achiever' : 'achievers'}
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: { xs: 'nowrap', md: 'wrap' },
                overflowX: { xs: 'auto', md: 'visible' },
                pb: { xs: 0.5, md: 0 },
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'divider' },
              }}
            >
              {filterChips.map((chip, index) => {
                const isActive = courseFilter === chip.value;

                return (
                  <Box
                    component={motion.div}
                    key={chip.value || 'all'}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: Math.min(index, 8) * 0.04, ease: 'easeOut' }}
                    sx={{ flexShrink: 0, display: 'flex' }}
                  >
                    <Chip
                      label={`${chip.label} (${chip.count})`}
                      onClick={() => handleCourseFilter(chip.value)}
                      color={isActive ? 'primary' : 'default'}
                      variant={isActive ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        transition: 'transform 0.2s ease, background-color 0.2s ease',
                        '@media (hover: hover)': { '&:hover': { transform: 'translateY(-2px)' } },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Card>
        </MotionInView>

        {filtered.length > 0 && (
          <Typography variant="body2" sx={{ color: 'text.disabled', mb: { xs: 2.5, md: 3 } }}>
            Showing <strong>{firstIndex}</strong>–<strong>{lastIndex}</strong> of <strong>{filtered.length}</strong>
            {courseFilter ? ` in ${courseFilter}` : ''}
          </Typography>
        )}

        {showLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 8, md: 12 } }}>
            <CircularProgress size={32} thickness={4} />
          </Box>
        ) : filtered.length > 0 ? (
          <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
            {paged.map((student, index) => (
              <StudentCard key={student?.id ?? index} student={student} index={index} />
            ))}
          </Grid>
        ) : (
          <MotionInView variants={varFadeInUp}>
            <Card sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                No achievers found
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
                Try a different course filter.
              </Typography>
              {courseFilter && (
                <Button variant="outlined" onClick={() => handleCourseFilter('')}>
                  Clear filter
                </Button>
              )}
            </Card>
          </MotionInView>
        )}

        {totalPages > 1 && (
          <MotionInView variants={varFadeInUp}>
            <Stack direction="row" sx={{ mt: { xs: 4, md: 5 }, justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                siblingCount={isUpSm ? 1 : 0}
                showFirstButton={isUpSm}
                showLastButton={isUpSm}
              />
            </Stack>
          </MotionInView>
        )}
      </Container>
    </>
  );
}
